import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Login = () => {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session, loading, signIn } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>);

  }

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { error } = await signIn(email, password);
    if (error) {
      toast.error(error.message);
      setIsSubmitting(false);
      return;
    }

    // Check if user is admin/sales to redirect to the right place
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: adminRow } = await supabase.
      from("admin_users").
      select("role").
      eq("user_id", user.id).
      maybeSingle();

      if (adminRow) {
        const dest = adminRow.role === "sales" ? "/admin/contracts" : "/admin";
        setIsSubmitting(false);
        navigate(dest, { replace: true });
        return;
      }
    }

    setIsSubmitting(false);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img alt="Trusted James" className="h-10 w-auto" src="/lovable-uploads/ea21ca82-cde8-4270-965a-e81d785ae160.png" />
          </Link>
          
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground mb-2">
              Welcome back
            </h1>
            <p className="text-muted-foreground">
              Sign in to access your dashboard and leads.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                required />

            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-accent hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-12"
                  required />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">

                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" variant="gold" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Sign In
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </form>
          
          <p className="text-center text-sm text-muted-foreground mt-8">
            Don't have an account?{" "}
            <Link to="/signup" className="text-accent font-medium hover:underline">
              Create one now
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 gradient-hero items-center justify-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-72 h-72 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 text-center max-w-md">
          <div className="w-24 h-24 rounded-3xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center mx-auto mb-8">
            <Shield className="w-12 h-12 text-accent" />
          </div>
          <h2 className="font-display text-3xl font-bold text-primary-foreground mb-4">
            Your leads await
          </h2>
          <p className="text-primary-foreground/70 leading-relaxed">
            Access your exclusive leads, manage your pipeline, and track your performance—all in one place.
          </p>
          
          <div className="mt-12 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-accent">68%+</p>
              <p className="text-xs text-primary-foreground/60">Contact Rate</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-accent">&lt; 5min</p>
              <p className="text-xs text-primary-foreground/60">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-accent">100+</p>
              <p className="text-xs text-primary-foreground/60">Active Agents</p>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default Login;